import { useCallback } from "react";
import { useLocation, useNavigate, type Location } from "react-router-dom";

/**
 * A call opened over the page it was opened from, which stays exactly where it
 * was underneath.
 *
 * A call is a dialog, and the shell has always rendered it over whatever was
 * there -- but the *route* for one is `/sales/calls/:id`, which renders the
 * unfiltered worklist. So opening a call from "Mine", from page two, from the
 * closed list or from the board swapped the screen underneath for a different
 * one, and closing it came back to a page that had been rebuilt from the top.
 * Reps working down a list lost their place every time they logged a call.
 *
 * The fix is React Router's background-location pattern. Opening a call from a
 * page passes that page's location in navigation state; `App` keeps rendering
 * the routes against it, so the list -- its filters, its page, its scroll --
 * never unmounts, and the call is drawn on top by a second, small set of
 * routes reading the real URL. Closing is a step back in history, which lands
 * on the background location itself, so React sees the same tree it already
 * had and nothing moves.
 *
 * Only for the places a call is *worked from*: the worklist and the board.
 * A call linked from a ticket, a CRM account or a meeting arrives with no
 * background and opens as before, over the list -- there is no sales page to
 * keep behind it, and rendering the ticket queue under a sales dialog would be
 * the wrong product entirely. A pasted link and a refresh have no state either,
 * and get the same.
 */

interface OverlayState {
  background?: Location;
}

/** The page a call was opened over, if it was opened over one. */
export function backgroundOf(location: Location): Location | null {
  const state = location.state as OverlayState | null;
  return state?.background ?? null;
}

/** Navigation state that keeps the current page underneath the call. */
export function useCallLinkState(): OverlayState {
  const location = useLocation();
  return { background: location };
}

/** Open a call over the page this is called from. */
export function useOpenCall(): (id: number) => void {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(
    (id: number) =>
      navigate(`/sales/calls/${id}`, { state: { background: location } }),
    [navigate, location],
  );
}

/**
 * Close the open call: back where it was opened from.
 *
 * A step back rather than a push, because back *is* the page underneath --
 * pushing it again would remount it and lose the scroll this exists to keep.
 * Falls back to the worklist when there is nothing in this tab's history to go
 * back to: a call opened from a pasted link would otherwise close by leaving
 * Pulse. `idx` is React Router's own position counter in `history.state`.
 */
export function useCloseCall(): () => void {
  const navigate = useNavigate();
  return useCallback(() => {
    const index = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (index > 0) navigate(-1);
    else navigate("/sales/calls", { replace: true });
  }, [navigate]);
}
